/**
 * Map Configuration
 * Tile servers and map settings - all configurable for future self-hosting
 */

import type { LatLngTuple } from 'leaflet';

// Tile server configurations
export const TILE_SERVERS = {
  osm: {
    id: 'osm',
    name: 'OpenStreetMap',
    url: import.meta.env.VITE_TILE_OSM_URL ?? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: import.meta.env.VITE_TILE_OSM_ATTRIBUTION ?? '© OpenStreetMap contributors',
  },
  satellite: {
    id: 'satellite',
    name: 'Спутник',
    url: import.meta.env.VITE_TILE_SATELLITE_URL ?? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: import.meta.env.VITE_TILE_SATELLITE_ATTRIBUTION ?? '© Esri',
  },
  satphoto: {
    id: 'satphoto',
    name: 'Спутник HD',
    url: import.meta.env.VITE_TILE_SATPHOTO_URL ?? '/api/satellite/tile/{z}/{x}/{y}',
    attribution: import.meta.env.VITE_TILE_SATPHOTO_ATTRIBUTION ?? '© Mapbox © Maxar',
  },
  dark: {
    id: 'dark',
    name: 'Тёмная',
    url: import.meta.env.VITE_TILE_DARK_URL ?? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: import.meta.env.VITE_TILE_DARK_ATTRIBUTION ?? '© OpenStreetMap contributors, © CARTO',
  },
} as const;

export type TileServerId = keyof typeof TILE_SERVERS;

// Map default settings
export const MAP_CONFIG = {
  // Madre de Dios, Peru - center of the region
  defaultCenter: [
    parseFloat(import.meta.env.VITE_MAP_DEFAULT_CENTER_LAT ?? '-12.59'),
    parseFloat(import.meta.env.VITE_MAP_DEFAULT_CENTER_LNG ?? '-69.19'),
  ] as LatLngTuple,
  defaultZoom: parseInt(import.meta.env.VITE_MAP_DEFAULT_ZOOM ?? '8', 10),
  minZoom: parseInt(import.meta.env.VITE_MAP_MIN_ZOOM ?? '3', 10),
  maxZoom: parseInt(import.meta.env.VITE_MAP_MAX_ZOOM ?? '26', 10),
  
  // Default tile layer
  defaultTileServer: 'dark' as TileServerId,
  
  // Cluster settings
  clusterRadius: 80,
  clusterMaxZoom: 12,
  
  // Interaction
  zoomControl: false, // We use custom controls
  attributionControl: true,
} as const;

// Layer colors (matching tailwind.config.js)
export const LAYER_COLORS = {
  reinfo: '#3b82f6',      // blue-500 - REINFO plots
  catastro: '#fbbf24',    // amber-400 - Catastro concessions
  forestal: '#15803d',    // green-700 - Forest concessions (darker for contrast)
  certificados: '#22d3ee', // cyan-400 - Environmental certificates (bright cyan)
  libre: '#a855f7',       // purple-500 - Libre denunciabilidad
  predios: '#eab308',     // yellow-500 - Predios rurales (agricultural land)
  comunidades: '#9333ea', // purple-600 - Comunidades (nativas/campesinas)
  sernanp: '#e74c3c',     // red - SERNANP Protected Areas
  water: '#3b82f6',       // blue-500 - Water bodies (lakes, rivers)
  waterInfra: '#0ea5e9',  // sky-500 - Water infrastructure (wells, dams)
  roads: '#dc2626',       // red-600 - Road network
  mining: '#3b82f6',      // blue-500 - Mining cadastre
  restrictions: '#f97316', // orange-500 - Restriction layers (archaeology, glaciers)
  userContent: '#ec4899', // pink-500 - User content
  elevation: '#78716c',   // stone-500 - Elevation/Terrain (hillshade)
} as const;

// Owner type colors
export const OWNER_COLORS = {
  person: '#3b82f6',   // blue-500
  company: '#8b5cf6',  // violet-500
  unknown: '#6b7280',  // gray-500
} as const;

// Status colors
export const STATUS_COLORS = {
  vigente: '#22c55e',    // green-500
  suspendido: '#eab308', // yellow-500
  extinto: '#ef4444',    // red-500
  bloqueado: '#f97316',  // orange-500
} as const;
